/**
 * Feature Store - Zustand
 *
 * Centralized state management for package features
 *
 * Features:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Real-time search and filtering
 * - Pagination support
 * - Category filtering
 * - Loading and error states
 * - Optimistic UI updates
 * - Toast notifications
 *
 * Backend API: /api/features
 *
 * @module useFeatureStore
 */

import { create } from 'zustand'
import { featureService } from '@services/featureService'
import toast from 'react-hot-toast'

/**
 * Feature Store
 *
 * State Management for Features
 */
export const useFeatureStore = create((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * List of all features
   * @type {Array<Object>}
   */
  features: [],

  /**
   * Currently selected feature for viewing/editing
   * @type {Object|null}
   */
  selectedFeature: null,

  /**
   * Loading state for async operations
   * @type {boolean}
   */
  loading: false,

  /**
   * Error message if operation fails
   * @type {string|null}
   */
  error: null,

  /**
   * Filter and search parameters
   * @type {Object}
   */
  filters: {
    search: '',              // Search by name/description
    status: 'all',           // all, active, archived
    category: 'all',         // all, or specific category
    sortBy: 'display_order', // Field to sort by
    sortOrder: 'asc',        // asc or desc
  },

  /**
   * Pagination configuration
   * @type {Object}
   */
  pagination: {
    page: 1,                 // Current page
    limit: 25,               // Items per page
    total: 0,                // Total number of items
    totalPages: 0,           // Total number of pages
  },

  /**
   * Statistics about features
   * @type {Object}
   */
  stats: {
    total: 0,
    active: 0,
    archived: 0,
    inUse: 0,
  },

  /**
   * Available categories
   * @type {Array<string>}
   */
  categories: [],

  // ============================================================================
  // ACTIONS - READ OPERATIONS
  // ============================================================================

  /**
   * Fetch all features with current filters
   *
   * @returns {Promise<void>}
   */
  fetchFeatures: async () => {
    set({ loading: true, error: null })

    try {
      const { filters, pagination } = get()

      // Build query parameters
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      }

      if (filters.search) {
        params.search = filters.search
      }

      if (filters.status !== 'all') {
        params.status = filters.status
      }

      if (filters.category !== 'all') {
        params.category = filters.category
      }

      // Make API request via service
      const response = await featureService.getAll(params)

      // Backend returns { status, message, data: [...], meta: {...} }
      const features = response.data || []
      const meta = response.meta || {}

      // Update state with response data
      set({
        features: features,
        pagination: {
          ...pagination,
          page: meta.page || pagination.page,
          total: meta.total || 0,
          totalPages: meta.total_pages || Math.ceil((meta.total || 0) / pagination.limit),
        },
        loading: false,
      })

      // Update statistics
      get().calculateStats()

      // Fetch categories if not already loaded
      if (get().categories.length === 0) {
        get().fetchCategories()
      }

    } catch (error) {
      console.error('Failed to fetch features:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load features'
      set({
        error: errorMessage,
        loading: false,
        features: [], // Set empty array on error
      })
      toast.error(errorMessage)
    }
  },

  /**
   * Fetch single feature by ID
   *
   * @param {number} id - Feature ID
   * @returns {Promise<Object>} Feature data
   */
  fetchFeatureById: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await featureService.getById(id)
      const feature = response.data || response

      set({
        selectedFeature: feature,
        loading: false,
      })

      return feature

    } catch (error) {
      console.error('Failed to fetch feature:', error)
      set({
        error: error.message || 'Feature not found',
        loading: false,
      })
      toast.error('Feature not found')
      throw error
    }
  },

  /**
   * Fetch feature by slug
   *
   * @param {string} slug - Feature slug
   * @returns {Promise<Object>} Feature data
   */
  fetchFeatureBySlug: async (slug) => {
    try {
      const response = await featureService.getBySlug(slug)
      const feature = response.data || response
      set({ selectedFeature: feature })
      return feature
    } catch (error) {
      console.error('Failed to fetch feature:', error)
      toast.error('Feature not found')
      throw error
    }
  },

  /**
   * Fetch all categories
   *
   * @returns {Promise<void>}
   */
  fetchCategories: async () => {
    try {
      const response = await featureService.getCategories()
      const categories = response.data || []
      set({ categories: Array.isArray(categories) ? categories : [] })
    } catch (error) {
      console.error('Failed to fetch categories:', error)
      set({ categories: [] }) // Set empty array on error
    }
  },

  // ============================================================================
  // ACTIONS - CREATE OPERATIONS
  // ============================================================================

  /**
   * Create new feature
   *
   * @param {Object} data - Feature data
   * @returns {Promise<Object>} Created feature
   */
  createFeature: async (data) => {
    set({ loading: true, error: null })

    try {
      const response = await featureService.create(data)
      const newFeature = response.data || response

      // Optimistic UI update - add to beginning of list
      set((state) => ({
        features: [newFeature, ...state.features],
        pagination: {
          ...state.pagination,
          total: state.pagination.total + 1,
        },
        loading: false,
      }))

      toast.success('Feature created successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh categories
      get().fetchCategories()

      return newFeature

    } catch (error) {
      console.error('Failed to create feature:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to create feature'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - UPDATE OPERATIONS
  // ============================================================================

  /**
   * Update existing feature
   *
   * @param {number} id - Feature ID
   * @param {Object} data - Updated feature data
   * @returns {Promise<Object>} Updated feature
   */
  updateFeature: async (id, data) => {
    set({ loading: true, error: null })

    try {
      const response = await featureService.update(id, data)
      const updatedFeature = response.data || response

      // Optimistic UI update - replace in list
      set((state) => ({
        features: state.features.map((feature) =>
          feature.id == id ? updatedFeature : feature
        ),
        selectedFeature: state.selectedFeature?.id == id
          ? updatedFeature
          : state.selectedFeature,
        loading: false,
      }))

      toast.success('Feature updated successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh categories if category changed
      if (data.category !== undefined) {
        get().fetchCategories()
      }

      return updatedFeature

    } catch (error) {
      console.error('Failed to update feature:', error)

      let errorMessage = 'Failed to update feature'

      if (error.response?.data) {
        const responseData = error.response.data

        if (responseData.messages) {
          if (typeof responseData.messages === 'object') {
            const errors = Object.values(responseData.messages).filter(msg => typeof msg === 'string')
            if (errors.length > 0) {
              errorMessage = errors.join(', ')
            }
          } else if (typeof responseData.messages === 'string') {
            errorMessage = responseData.messages
          }
        } else if (responseData.message) {
          errorMessage = responseData.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - DELETE OPERATIONS
  // ============================================================================

  /**
   * Archive feature (change status to archived)
   *
   * @param {number} id - Feature ID
   * @returns {Promise<void>}
   */
  archiveFeature: async (id) => {
    set({ loading: true, error: null })

    try {
      await featureService.archive(id)

      // Optimistic UI update
      set((state) => ({
        features: state.features.map((feature) =>
          feature.id == id ? { ...feature, status: 'archived' } : feature
        ),
        selectedFeature: state.selectedFeature?.id == id ? null : state.selectedFeature,
        loading: false,
      }))

      toast.success('Feature archived successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters
      setTimeout(() => get().fetchFeatures(), 100)

    } catch (error) {
      console.error('Failed to archive feature:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to archive feature'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Delete (soft delete) feature
   *
   * @param {number} id - Feature ID
   * @returns {Promise<void>}
   */
  deleteFeature: async (id) => {
    set({ loading: true, error: null })

    try {
      await featureService.delete(id)

      // Optimistic UI update
      set((state) => ({
        features: state.features.map((feature) =>
          feature.id == id ? { ...feature, deleted_at: new Date().toISOString() } : feature
        ),
        selectedFeature: state.selectedFeature?.id == id ? null : state.selectedFeature,
        loading: false,
      }))

      toast.success('Feature deleted successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters
      setTimeout(() => get().fetchFeatures(), 100)

    } catch (error) {
      console.error('Failed to delete feature:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete feature'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Restore archived feature
   *
   * @param {number} id - Feature ID
   * @returns {Promise<void>}
   */
  restoreFeature: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await featureService.restore(id)
      const restored = response.data || response

      // Optimistic UI update
      set((state) => ({
        features: state.features.map((feature) =>
          feature.id == id ? restored : feature
        ),
        selectedFeature: state.selectedFeature?.id == id ? null : state.selectedFeature,
        loading: false,
      }))

      toast.success('Feature restored successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters
      setTimeout(() => get().fetchFeatures(), 100)

    } catch (error) {
      console.error('Failed to restore feature:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to restore feature'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - FILTER & SEARCH
  // ============================================================================

  /**
   * Update search/filter parameters and refresh data
   *
   * @param {Object} newFilters - New filter values
   */
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 }, // Reset to page 1 on filter change
    }))

    // Fetch with new filters
    get().fetchFeatures()
  },

  /**
   * Update search term
   *
   * @param {string} searchTerm - Search query
   */
  setSearch: (searchTerm) => {
    get().setFilters({ search: searchTerm })
  },

  /**
   * Update status filter
   *
   * @param {string} status - Status filter (all, active, archived)
   */
  setStatusFilter: (status) => {
    get().setFilters({ status })
  },

  /**
   * Update category filter
   *
   * @param {string} category - Category filter (all, or specific category)
   */
  setCategoryFilter: (category) => {
    get().setFilters({ category })
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({
      filters: {
        search: '',
        status: 'all',
        category: 'all',
        sortBy: 'display_order',
        sortOrder: 'asc',
      },
      pagination: {
        ...get().pagination,
        page: 1,
      },
    })

    get().fetchFeatures()
  },

  // ============================================================================
  // ACTIONS - PAGINATION
  // ============================================================================

  /**
   * Go to specific page
   *
   * @param {number} page - Page number
   */
  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }))

    get().fetchFeatures()
  },

  /**
   * Go to next page
   */
  nextPage: () => {
    const { pagination } = get()
    if (pagination.page < pagination.totalPages) {
      get().setPage(pagination.page + 1)
    }
  },

  /**
   * Go to previous page
   */
  prevPage: () => {
    const { pagination } = get()
    if (pagination.page > 1) {
      get().setPage(pagination.page - 1)
    }
  },

  // ============================================================================
  // ACTIONS - UTILITIES
  // ============================================================================

  /**
   * Calculate statistics from current features
   */
  calculateStats: () => {
    const { features } = get()

    const stats = {
      total: features.length,
      active: features.filter((feature) => feature.status === 'active').length,
      archived: features.filter((feature) => feature.status === 'archived').length,
      inUse: features.filter((feature) => feature.usage_stats?.package_count > 0).length,
    }

    set({ stats })
  },

  /**
   * Select feature for viewing/editing
   *
   * @param {Object|null} feature - Feature to select
   */
  selectFeature: (feature) => {
    set({ selectedFeature: feature })
  },

  /**
   * Clear selected feature
   */
  clearSelection: () => {
    set({ selectedFeature: null })
  },

  /**
   * Reset entire store to initial state
   */
  reset: () => {
    set({
      features: [],
      selectedFeature: null,
      loading: false,
      error: null,
      filters: {
        search: '',
        status: 'all',
        category: 'all',
        sortBy: 'display_order',
        sortOrder: 'asc',
      },
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 0,
      },
      stats: {
        total: 0,
        active: 0,
        archived: 0,
        inUse: 0,
      },
      categories: [],
    })
  },
}))

export default useFeatureStore

